import React from 'react';
import { cn } from '../lib/utils/cn';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      [elemName: string]: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

interface TypographyProps {
  className?: string;
  children: React.ReactNode;
}

interface HeadingProps extends TypographyProps {
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  gradient?: boolean;
}

const Heading: React.FC<HeadingProps> = ({
  level = 1,
  gradient = false,
  className,
  children,
  ...props
}) => {
  const levels = {
    1: 'text-4xl md:text-5xl lg:text-6xl font-bold',
    2: 'text-3xl md:text-4xl font-bold',
    3: 'text-2xl md:text-3xl font-bold',
    4: 'text-xl md:text-2xl font-semibold',
    5: 'text-lg md:text-xl font-semibold',
    6: 'text-base md:text-lg font-semibold'
  };

  const classes = cn(
    levels[level],
    gradient ? 'bg-gradient-to-r from-brand-600 to-accent-600 bg-clip-text text-transparent' : 'text-gray-900',
    className
  );

  const Tag = `h${level}` as const;

  return React.createElement(
    Tag,
    { className: classes, ...props },
    children
  );
};

interface TextProps extends TypographyProps {
  variant?: 'default' | 'muted' | 'subtle' | 'lead';
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
}

const Text: React.FC<TextProps> = ({
  variant = 'default',
  size = 'base',
  className,
  children,
  ...props
}) => {
  const variants = {
    default: 'text-gray-900',
    muted: 'text-gray-600',
    subtle: 'text-gray-500',
    lead: 'text-gray-700 font-medium'
  };

  const sizes = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };

  const classes = cn(
    variants[variant],
    sizes[size],
    className
  );

  return (
    <p className={classes} {...props}>
      {children}
    </p>
  );
};

export { Heading, Text };